import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { ChoiceList, TextArea } from "../empieza/Fields";
import { WEEK_DAYS, YESNO } from "../../lib/onboardingSchema";
import { MealTimes } from "./MealTimes";
import { VideoUploader } from "./VideoUploader";

const DURATIONS = ["45 min", "60 min", "75 min", "90 min", "105 min", "120 min o más"];

const Label = ({ f }) => (
  <>
    <label style={{ fontSize: 13, letterSpacing: "0.04em", textTransform: "none", color: "#e5e5e5", fontWeight: 600 }}>
      {f.label}{f.req && <span style={{ color: "#E11D2E" }}> *</span>}
    </label>
    {f.help && <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: -4 }}>{f.help}</div>}
  </>
);

const Days = ({ value = [], onChange, testid }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
    {WEEK_DAYS.map((d) => {
      const on = value.includes(d);
      return (
        <button key={d} type="button" className={`emp-age${on ? " on" : ""}`} style={{ fontSize: 16, height: 48 }}
          onClick={() => onChange(on ? value.filter((x) => x !== d) : [...value, d])} data-testid={`${testid}-${d.slice(0, 2).toLowerCase()}`}>
          {d.slice(0, 2)}
        </button>
      );
    })}
  </div>
);

const Sessions = ({ days = [], value = {}, onChange, testid, emptyText }) => days.length === 0 ? (
  <div style={{ fontSize: 13, color: "#737373" }}>{emptyText || "Marca primero los días de entrenamiento."}</div>
) : (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    {WEEK_DAYS.filter((d) => days.includes(d)).map((d) => (
      <div key={d} style={{ display: "grid", gridTemplateColumns: "84px 1fr 1fr", gap: 8, alignItems: "center" }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{d}</div>
        <input type="time" className="emp-input" style={{ padding: 12 }} value={value[d]?.time || ""} data-testid={`${testid}-${d}-time`}
          onChange={(e) => onChange({ ...value, [d]: { ...value[d], time: e.target.value } })} />
        <select className="emp-select" style={{ padding: 12 }} value={value[d]?.duration || ""} data-testid={`${testid}-${d}-duration`}
          onChange={(e) => onChange({ ...value, [d]: { ...value[d], duration: e.target.value } })}>
          <option value="">Duración</option>
          {DURATIONS.map((x) => <option key={x}>{x}</option>)}
        </select>
      </div>
    ))}
  </div>
);

const SubField = ({ f, value, onChange, testid }) => (
  <div className="emp-field" style={{ marginBottom: 10 }}>
    <Label f={f} />
    {f.type === "single" ? <ChoiceList options={f.opts} value={value || ""} onChange={onChange} prefix={testid} />
      : <input className="emp-input" type={f.type === "date" ? "date" : "text"} value={value || ""} maxLength={200}
          placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />}
  </div>
);

const Repeat = ({ f, value = [], onChange, testid }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    {value.map((item, i) => (
      <div key={i} style={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, padding: 14, background: "rgba(255,255,255,0.02)" }} data-testid={`${testid}-item-${i}`}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: 11, letterSpacing: "0.2em", color: "#E11D2E", fontWeight: 700 }}>#{i + 1}</span>
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}
            style={{ background: "none", border: "none", color: "#a3a3a3", cursor: "pointer" }} aria-label="Eliminar"><Trash2 size={16} /></button>
        </div>
        {f.fields.map((sf) => (
          <SubField key={sf.k} f={sf} value={item[sf.k]} testid={`${testid}-${i}-${sf.k}`}
            onChange={(v) => onChange(value.map((it, j) => (j === i ? { ...it, [sf.k]: v } : it)))} />
        ))}
      </div>
    ))}
    <button type="button" className="emp-opt" style={{ justifyContent: "center", minHeight: 50 }} onClick={() => onChange([...value, {}])} data-testid={`${testid}-add`}>
      <Plus size={16} /> {f.addLabel || "Añadir"}
    </button>
  </div>
);

export const Field = ({ f, value, onChange, data, testid, files, onFiles }) => {
  const t = testid || `onb-${f.k}`;
  let input;
  switch (f.type) {
    case "text":
      input = <input className="emp-input" value={value || ""} maxLength={200} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={t} />;
      break;
    case "textarea":
      input = <TextArea value={value || ""} onChange={onChange} max={f.max || 400} rows={3} placeholder={f.placeholder || "Escribe aquí…"} testid={t} />;
      break;
    case "number":
      input = (
        <div style={{ position: "relative" }}>
          <input className="emp-input" type="number" inputMode="decimal" min={f.min} max={f.max} step={f.step || 1} value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={t} style={{ paddingRight: 56 }} />
          {f.unit && <span style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", color: "#737373", fontSize: 14 }}>{f.unit}</span>}
        </div>
      );
      break;
    case "date":
      input = <input className="emp-input" type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={t} />;
      break;
    case "single":
      input = <ChoiceList options={f.opts} value={value || ""} onChange={onChange} prefix={t} />;
      break;
    case "multi":
      input = <ChoiceList multi options={f.opts} value={value || []} onChange={onChange} prefix={t} />;
      break;
    case "yesno":
      input = <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}><ChoiceList options={YESNO} value={value || ""} onChange={onChange} prefix={t} /></div>;
      break;
    case "days":
      input = <Days value={value || []} onChange={onChange} testid={t} />;
      break;
    case "sessions":
      input = <Sessions days={data?.[f.daysFrom] || []} value={value || {}} onChange={onChange} testid={t} emptyText={f.emptyText} />;
      break;
    case "mealtimes":
      input = <MealTimes value={value || {}} onChange={onChange} testid={t} />;
      break;
    case "repeat":
      input = <Repeat f={f} value={value || []} onChange={onChange} testid={t} />;
      break;
    case "videos":
      input = <VideoUploader files={files} onFiles={onFiles} />;
      break;
    default:
      input = null;
  }
  return (
    <div className="emp-field" style={{ marginBottom: 22 }}>
      <Label f={f} />
      {input}
    </div>
  );
};
